import { Box, Stack } from '@mui/material';
import { Caption } from '@telegram-apps/telegram-ui';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListCategory } from '../../client/types';
import { getColor } from '../../helpers/color';
import { getCategorySummaries } from '../../helpers/quest';
import { CompletedQuestSummary } from '../../hooks/useDailyProgress';
import { todayQuestProgressPath } from '../../navigation/paths';
import { CategoryIcon } from '../common/CategoryIcon';
import { ThemedSkeleton } from '@focuslee/shared-mini-app';
import { QuestSummary } from './QuestSummary';

export type TodayQuestProgressProps = {
  doneSum: number;
  totalSum: number;
  summaryPercentage: number;
  totalXP: number;
  completedQuests: CompletedQuestSummary[];
  allQuestsCompleted: boolean;
  categoryMap: Record<string, ListCategory>;
  loading?: boolean;
};

export const TodayQuestProgress: React.FC<TodayQuestProgressProps> = ({
  doneSum,
  totalSum,
  summaryPercentage,
  totalXP,
  completedQuests,
  allQuestsCompleted,
  categoryMap,
  loading = false,
}) => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const categorySummaries = useMemo(() => {
        return getCategorySummaries(completedQuests, categoryMap);
    }, [completedQuests, categoryMap]);

    const topFour = categorySummaries.slice(0, 4);
    const moreCount = Math.max(categorySummaries.length - 4, 0);

    const handleClick = () => {
        navigate(todayQuestProgressPath);
    };

    return (
        <Stack direction="column" sx={allQuestsCompleted ? { flex: 1, minHeight: 0 } : undefined}>
            <Stack
                direction="column"
                padding="0px 20px 15px 20px"
                gap={1.5}
                borderBottom="1px solid var(--tg-theme-section-bg-color)"
                onClick={handleClick}
                sx={{ cursor: 'pointer' }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Caption style={{ fontSize: '16px', fontWeight: '500' }}>{t('quest.todayProgress')}</Caption>
                    {loading ? (
                        <ThemedSkeleton variant="text" width={30} height={18} />
                    ) : (
                        <Caption style={{ fontSize: '16px', fontWeight: '700', color: 'var(--tgui--button_color)' }}>
                            {summaryPercentage}%
                        </Caption>
                    )}
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    {loading ? (
                        <ThemedSkeleton variant="text" width={140} height={15} />
                    ) : (
                        <Caption style={{ fontSize: '14px', color: 'var(--tgui--hint_color)' }}>
                            {t('quest.completedProgress', { done: doneSum, total: totalSum })}
                        </Caption>
                    )}

                    <Stack direction="row" alignItems="center" gap={0.5} minWidth={categorySummaries.length > 0 ? undefined : 0}>
                        {loading ? (
                            <>
                                {[0, 1, 2, 3].map((i) => (
                                    <ThemedSkeleton key={i} variant="rectangular" width={20} height={20} />
                                ))}
                            </>
                        ) : categorySummaries.length > 0 ? (
                            <>
                                {topFour.map(c => (
                                    <Box key={c.categoryId} sx={{ width: 20, height: 20, borderRadius: 1, background: getColor(c.categoryColor), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CategoryIcon icon={c.categoryIcon} iconColor={c.categoryColor} customIcon={c.categoryCustomIcon} type="filled" size={14} color="var(--tg-theme-text-color)" />
                                    </Box>
                                ))}
                                {moreCount > 0 && (
                                    <Box sx={{ width: 20, height: 20, borderRadius: 1, background: 'var(--tgui--hint_color)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Caption style={{ fontSize: '10px', fontWeight: 'bold' }}>+{moreCount}</Caption>
                                    </Box>
                                )}
                            </>
                        ) : null}
                    </Stack>
                </Stack>
            </Stack>
            {allQuestsCompleted && !loading && (
                <Stack flex={1} minHeight={0} alignItems="center" justifyContent="center">
                    <QuestSummary
                        totalXP={totalXP}
                        categorySummaries={categorySummaries}
                    />
                </Stack>
            )}
        </Stack>
    );
};
